<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import CampaignPreview from '@/components/CampaignPreview.vue'
import CampaignAdvices from '../CampaignAdvices.vue'
import CampaignTemplates from '../CampaignTemplates.vue'
import CampaignMessageBlock from '../components/CampaignMessageBlock.vue'
import CampaignMessageOptionsBlock from '../components/CampaignMessageOptionsBlock.vue'
import { getCampaignPreviewText } from '../utils/campaignMessage'

import type { CampaignSelection, CampaignType, LoyaltyReward, QrCodeProvider, Survey } from '@/types/types'

type RewardId = number | null

const props = defineProps<{
  selected: CampaignSelection
  messageContent: string
  messageContentRef?: HTMLTextAreaElement
  selectedReward?: RewardId
  messageLength: number
  conversionDelay: number
  rewards: LoyaltyReward[]
  surveys?: Survey[] | null
  surveysLoading?: boolean
  surveysError?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue'): void
  (e: 'update:messageContentRef', value: HTMLTextAreaElement): void
  (e: 'update:messageContent', value: string): void
  (e: 'update:messageLength', value: number): void
  (e: 'update:selectedReward', value: RewardId): void
  (e: 'onCreateShortLink', text: string): void
  (e: 'onAddShortLink', text: string): void
  (e: 'hasMessageContentError', value: boolean): void
  (e: 'reward-upserted', value: LoyaltyReward): void
}>()

const route = useRoute()
const { t } = useI18n()
const { stores } = useLocalStorage()

const messageContentInput = ref<HTMLTextAreaElement | null>(null)
const campaignMessageBlockRef = ref<typeof CampaignMessageBlock | null>(null)
const { isQrCodeUnique, updateQrCode }: QrCodeProvider = inject('qrCode')
const campaignType = computed<CampaignType>(() => (route.query.type as CampaignType) || 'sms')

const messageContentModel = useVModel(props, 'messageContent', emit)
const messageContentRefModel = useVModel(props, 'messageContentRef', emit)
const selectedRewardModel = useVModel(props, 'selectedReward', emit)
const messageLength = useVModel(props, 'messageLength', emit)
const conversionDelay = useVModel(props, 'conversionDelay', emit)

const rewardModel = computed({
  get: () => ({
    selectedReward: selectedRewardModel.value,
    messageContent: messageContentModel.value
  }),
  set: (value) => {
    if (value.selectedReward !== undefined) {
      selectedRewardModel.value = value.selectedReward
    }
    if (value.messageContent !== undefined) {
      messageContentModel.value = value.messageContent
    }
  }
})

const rewardName = computed(() => {
  return props.rewards?.find((r) => r.id === selectedRewardModel.value)?.name
})

const getQrCodeLink = (isActive: boolean) => {
  const translatedMessage = t('pages.campaign.campaignCreatePage.block.qrCodeBlock.placeholder')
  const placeholder = '{{ qrcodeLink }}'
  const pattern = ` ${translatedMessage} ${placeholder}`
  if (!isActive) {
    messageContentModel.value = messageContentModel.value.replace(pattern, '').replaceAll(placeholder, '')
  } else {
    messageContentModel.value += pattern
  }
}

const handleMessageLengthCalculation = () => {
  campaignMessageBlockRef.value?.handleMessageLengthCalculation()
}

const updateMessage = (message: string) => {
  messageContentModel.value = message
  document.querySelector('main')?.scroll({
    top: 150,
    left: 0,
    behavior: 'smooth'
  })
  setTimeout(() => {
    messageContentInput.value?.focus()
  }, 300)
}

const checkAllowedCharacters = () => {
  messageContentRefModel.value = messageContentInput.value
}

const selectedStoreIds = computed(() => {
  return props.selected.stores.map((store) => (store as any).value)
})

const longestStoreName = computed(() => {
  return stores.value
    .filter((store) => selectedStoreIds.value.includes(store.id))
    .reduce((longest, current) => (current.name.length > longest.name.length ? current : longest), {
      name: ''
    }).name
})

const messageContentPlaceholder = computed(() => {
  return getCampaignPreviewText(
    messageContentModel.value,
    longestStoreName.value,
    props.rewards?.find((r) => r.id === props.selectedReward)
  )
})

watch(messageContentModel, () => {
  if (!messageContentModel.value?.includes('{{ qrcodeLink }}') && isQrCodeUnique.value === true) {
    updateQrCode()
  }

  if (messageContentModel.value?.includes('{{ qrcodeLink }}') && isQrCodeUnique.value === false) {
    updateQrCode()
  }
})

watch(
  () => props.messageContent,
  () => checkAllowedCharacters()
)

watch(
  () => selectedRewardModel.value,
  (newRewardId) => {
    if (newRewardId && props.rewards?.length > 0) {
      const reward = props.rewards.find((r) => r.id === newRewardId)
      if (reward) {
        const currentMessage = messageContentModel.value || ''
        let updatedMessage = currentMessage

        if (!updatedMessage.includes('{{ rewardLink }}')) {
          updatedMessage += updatedMessage ? ' {{ rewardLink }}' : '{{ rewardLink }}'
        }

        messageContentModel.value = updatedMessage
      }
    } else if (!newRewardId) {
      const currentMessage = messageContentModel.value || ''
      const updatedMessage = currentMessage
        .replaceAll('{{ rewardLink }}', '')
        .replaceAll('{{ reward }}', '')
        .replaceAll('{{ voucher }}', '')
        .replaceAll(/  +/g, ' ')
        .trim()

      messageContentModel.value = updatedMessage
    }
  }
)

onMounted(() => {
  messageContentRefModel.value = messageContentInput.value
})
</script>

<template>
  <section class="space-y-6">
    <CampaignMessageBlock
      ref="campaignMessageBlockRef"
      v-model:message-content="messageContentModel"
      v-model:message-content-ref="messageContentInput"
      v-model:message-length="messageLength"
      :selected-store-ids="selected.stores.map((store) => (store as any).value)"
      :selected-reward="selectedReward"
      :rewards="rewards"
      :surveys="surveys"
      :surveys-loading="surveysLoading"
      :surveys-error="surveysError"
      class="transform transition-all duration-200"
      @has-message-content-error="emit('hasMessageContentError', $event)"
    />

    <CampaignMessageOptionsBlock
      v-model="rewardModel"
      v-model:conversion-delay="conversionDelay"
      :stores="stores"
      :campaign-type="campaignType"
      @handle-qr-code-link="getQrCodeLink(isQrCodeUnique)"
      @update:model-value="handleMessageLengthCalculation"
      @reward-upserted="emit('reward-upserted', $event)"
    />

    <div v-if="campaignType === 'sms'" aria-labelledby="announcements-title">
      <CampaignTemplates @update:message="updateMessage" />
    </div>

    <div v-if="campaignType === 'sms'" aria-labelledby="announcements-title">
      <CampaignAdvices />
    </div>

    <template v-if="campaignType === 'push'">
      <div class="bg-white rounded-lg border border-border">
        <div class="border-b border-border px-6 py-5">
          <h3 class="text-lg font-semibold text-foreground">
            {{ t('pages.campaign.campaignCreatePage.block.preview.title') }}
          </h3>
        </div>
        <div class="relative h-72 overflow-hidden">
          <CampaignPreview
            :message="messageContentPlaceholder"
            :raw-message="messageContentModel"
            :reward-name="rewardName"
            :type="campaignType"
            class="h-full"
          />
        </div>
      </div>
    </template>
  </section>
</template>
